import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Link2, Gauge, CalendarClock, Archive, AlertTriangle, Clock } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import { useAuth } from "@/context/AuthContext";
import { STATUS_COLOR } from "./PromiseTracker";
import { formatDateOnly } from "@/lib/dates";
import { EntityLinkManager } from "@/components/EntityLinkManager";

const CT_VERDICT_COLOR = {
  unverified: "text-zinc-400", true: "text-emerald-400", mostly_true: "text-emerald-300",
  misleading: "text-amber-400", mostly_false: "text-orange-400", false: "text-red-400",
  unsubstantiated: "text-fuchsia-400", out_of_context: "text-sky-400",
};

function Section({ title, children, action }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3"><h3 className="font-tactical font-bold text-sm">{title}</h3>{action}</div>
      {children}
    </Card>
  );
}

const LINK_MAP = {
  source: { api: "/sources", key: "linked_sources", label: (o) => o.title },
  claim: { api: "/claims", key: "linked_claims", label: (o) => o.statement },
  media: { api: "/media/assets", key: "linked_media", label: (o) => o.original_filename },
  intel: { api: "/ghana-intel/records", key: "linked_intel", label: (o) => o.title },
  claim_tracker: { api: "/claim-tracker/entries", key: "linked_claim_tracker", label: (o) => `${o.speaker}: ${o.claim_summary || o.claim_text || ""}` },
};

export default function PromiseTrackerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canReview = user?.roles?.some((r) => ["super_admin", "editor"].includes(r));
  const [meta, setMeta] = useState(null);
  const [rec, setRec] = useState(null);
  const [groups, setGroups] = useState([]);
  const [linkModal, setLinkModal] = useState(null);
  const [progressModal, setProgressModal] = useState(false);
  const [progressForm, setProgressForm] = useState({ status: "", progress_summary: "", completion_pct: "", notes: "" });
  const [reviseModal, setReviseModal] = useState(false);
  const [reviseForm, setReviseForm] = useState({ deadline: "", original_cost: "", current_cost: "" });

  const load = useCallback(() => {
    api.get(`/promise-tracker/promises/${id}`).then((r) => setRec(r.data)).catch(() => toast.error("Not found"));
  }, [id]);
  useEffect(() => {
    api.get("/promise-tracker/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/promise-tracker/groups").then((r) => setGroups(r.data)).catch(() => {});
    load();
  }, [load]);

  const slabel = (s) => meta?.status_labels?.[s] || s;

  const saveField = async (patch, msg = "Saved") => {
    try { await api.patch(`/promise-tracker/promises/${id}`, patch); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const submitProgress = async () => {
    try {
      const payload = { ...progressForm };
      payload.completion_pct = payload.completion_pct === "" ? null : Number(payload.completion_pct);
      await api.post(`/promise-tracker/promises/${id}/progress`, payload);
      toast.success("Progress recorded"); setProgressModal(false);
      setProgressForm({ status: "", progress_summary: "", completion_pct: "", notes: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const submitRevise = async () => {
    try {
      const payload = {};
      if (reviseForm.deadline) payload.deadline = reviseForm.deadline;
      if (reviseForm.original_cost !== "") payload.original_cost = Number(reviseForm.original_cost);
      if (reviseForm.current_cost !== "") payload.current_cost = Number(reviseForm.current_cost);
      await api.post(`/promise-tracker/promises/${id}/revise`, payload);
      toast.success("Revision recorded"); setReviseModal(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const archive = async () => {
    try { await api.delete(`/promise-tracker/promises/${id}`); toast.success("Archived"); nav("/command-center/promise-tracker"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openLink = async (type) => {
    try {
      const { data } = await api.get(LINK_MAP[type].api);
      const selected = (rec[LINK_MAP[type].key] || []).map((x) => x.id);
      setLinkModal({ type, options: data, selected });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveLinks = async () => {
    try {
      await api.post(`/promise-tracker/promises/${id}/links`, { link_type: linkModal.type, ids: linkModal.selected });
      toast.success("Links updated"); setLinkModal(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleSel = (v) => setLinkModal((m) => ({ ...m, selected: m.selected.includes(v) ? m.selected.filter((x) => x !== v) : [...m.selected, v] }));

  if (!rec) return <div className="p-6 text-zinc-500">Loading…</div>;

  const linkSections = [
    ["Linked Sources", "source", "pr-link-source", rec.linked_sources, (s) => s.title],
    ["Linked Claims & Evidence", "claim", "pr-link-claim", rec.linked_claims, (c) => `${c.statement} — ${c.evidence?.length || 0} evidence`],
    ["Linked Ghana Intelligence", "intel", "pr-link-intel", rec.linked_intel, (r) => `${r.title} (${r.status})`],
    ["Linked Media", "media", "pr-link-media", rec.linked_media, (m) => `${m.original_filename} (${m.visibility})`],
  ];
  const vlabel = (v) => meta?.claim_verdict_labels?.[v] || v;
  const dlFlag = rec.deadline_flag;

  return (
    <div className="p-6" data-testid="pr-detail">
      <button onClick={() => nav("/command-center/promise-tracker")} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> Promise Tracker</button>
      <PageHeader title={rec.title} subtitle={[rec.promisor, rec.promisor_role, rec.party, rec.administration].filter(Boolean).join(" · ")}>
        {canReview && <Btn variant="danger" data-testid="pr-archive-btn" onClick={archive}><Archive className="h-4 w-4" /> Archive</Btn>}
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Commitment">
            <p className="text-sm text-zinc-200 mb-3" data-testid="pr-text-display">“{rec.promise_text}”</p>
            <Field label="Normalized summary">
              <textarea data-testid="pr-summary-edit" className={inputCls} rows={2} defaultValue={rec.summary || ""}
                onBlur={(e) => e.target.value !== (rec.summary || "") && saveField({ summary: e.target.value }, "Summary saved")} />
            </Field>
            <Field label="Analyst notes">
              <textarea data-testid="pr-notes-edit" className={inputCls} rows={2} defaultValue={rec.analyst_notes || ""}
                onBlur={(e) => e.target.value !== (rec.analyst_notes || "") && saveField({ analyst_notes: e.target.value }, "Notes saved")} />
            </Field>
          </Section>

          <Section title="Fulfilment Status"
            action={canReview && <div className="flex gap-2">
              <Btn variant="ghost" data-testid="pr-open-revise" onClick={() => { setReviseForm({ deadline: rec.deadline || "", original_cost: rec.original_cost ?? "", current_cost: rec.current_cost ?? "" }); setReviseModal(true); }}><CalendarClock className="h-4 w-4" /> Revise</Btn>
              <Btn data-testid="pr-open-progress" onClick={() => { setProgressForm({ status: rec.status, progress_summary: "", completion_pct: "", notes: "" }); setProgressModal(true); }}><Gauge className="h-4 w-4" /> Record Progress</Btn>
            </div>}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="label-caps text-zinc-500">Status</span>
              <span data-testid="pr-current-status" className={`font-mono-data uppercase text-sm ${STATUS_COLOR[rec.status] || "text-zinc-400"}`}>{slabel(rec.status)}</span>
              {rec.deadline && <><span className="label-caps text-zinc-500 ml-3">Deadline</span><span className="text-sm text-zinc-300">{formatDateOnly(rec.deadline)}</span></>}
              {dlFlag === "overdue" && <span data-testid="pr-deadline-overdue" className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-sm border border-red-500/50 bg-red-500/15 text-red-300"><AlertTriangle className="h-3.5 w-3.5" /> Overdue</span>}
              {dlFlag === "due_soon" && <span data-testid="pr-deadline-duesoon" className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-sm border border-amber-500/50 bg-amber-500/15 text-amber-300"><Clock className="h-3.5 w-3.5" /> Due soon</span>}
              {!rec.has_supporting_evidence && <span className="text-xs text-amber-400">(link a source/claim to record an assessment)</span>}
            </div>
            <p className="text-[0.7rem] text-zinc-600 mt-2">Deadline alerts are derived indicators only — they never change the fulfilment status.</p>
            {!canReview && <p className="text-xs text-zinc-600 mt-2">Status, progress, deadline and cost changes require an editor.</p>}
          </Section>

          <Section title="Progress History">
            {rec.progress_updates.length === 0 ? <p className="text-xs text-zinc-600">No progress recorded yet.</p> : (
              <ol className="relative border-l border-zinc-800 ml-2 space-y-4" data-testid="pr-progress-history">
                {rec.progress_updates.map((u) => (
                  <li key={u.id} className="ml-4">
                    <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-emerald-500" />
                    <div className="flex items-center gap-2">
                      <span className={`font-mono-data uppercase text-xs ${STATUS_COLOR[u.status] || ""}`}>{slabel(u.status)}</span>
                      {u.completion_pct != null && <span className="text-xs text-zinc-500">{u.completion_pct}%</span>}
                      <span className="text-xs text-zinc-600">{u.update_date ? new Date(u.update_date).toLocaleDateString() : ""}</span>
                    </div>
                    {u.progress_summary && <p className="text-sm text-zinc-300 mt-1">{u.progress_summary}</p>}
                    {u.notes && <p className="text-xs text-zinc-500 mt-0.5">{u.notes}</p>}
                  </li>
                ))}
              </ol>
            )}
          </Section>

          {linkSections.map(([title, type, tid, items, render]) => (
            <Section key={type} title={title} action={<Btn variant="ghost" data-testid={tid} onClick={() => openLink(type)}><Link2 className="h-4 w-4" /> Manage</Btn>}>
              {items.length === 0 ? <p className="text-xs text-zinc-600">Nothing linked.</p> :
                <ul className="space-y-1 text-sm">{items.map((it) => <li key={it.id} className="text-zinc-300">• {render(it)}</li>)}</ul>}
            </Section>
          ))}

          <Section title="Linked Political Claims" action={<Btn variant="ghost" data-testid="pr-link-ct" onClick={() => openLink("claim_tracker")}><Link2 className="h-4 w-4" /> Manage</Btn>}>
            <p className="text-[0.7rem] text-zinc-600 mb-3">What was said (Claim verdict) vs. what was delivered (Promise status). These records stay independent — verdicts shown here are read live from the Political Claim Tracker.</p>
            {rec.linked_claim_tracker.length === 0 ? <p className="text-xs text-zinc-600">No political claims linked.</p> : (
              <div className="space-y-3" data-testid="pr-ct-list">
                {rec.linked_claim_tracker.map((c) => (
                  <div key={c.id} data-testid={`pr-ct-${c.id}`} className="border border-zinc-800 rounded-sm p-3 hover:border-zinc-600 cursor-pointer" onClick={() => nav(`/command-center/claim-tracker/${c.id}`)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-zinc-100">{c.speaker}{c.speaker_role ? <span className="text-zinc-500 font-normal"> · {c.speaker_role}</span> : ""}</div>
                        <div className="text-xs text-zinc-500">{[c.party, c.institution].filter(Boolean).join(" / ")}{c.date_made ? ` · ${formatDateOnly(c.date_made)}` : ""}{c.is_archived ? " · archived" : ""}</div>
                      </div>
                    </div>
                    <p className="text-sm text-zinc-300 mt-2">“{c.claim_summary || c.claim_text}”</p>
                    <div className="flex flex-wrap items-center gap-4 mt-3 pt-2 border-t border-zinc-800/70">
                      <div className="flex items-center gap-1.5">
                        <span className="label-caps text-zinc-600">Claim Verdict</span>
                        <span data-testid={`pr-ct-verdict-${c.id}`} className={`font-mono-data uppercase text-xs ${CT_VERDICT_COLOR[c.verdict] || "text-zinc-400"}`}>{vlabel(c.verdict)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="label-caps text-zinc-600">Claim Status</span>
                        <span className="font-mono-data uppercase text-xs text-zinc-400">{c.status}</span>
                      </div>
                      <div className="flex items-center gap-1.5 ml-auto">
                        <span className="label-caps text-zinc-600">Promise Fulfilment</span>
                        <span className={`font-mono-data uppercase text-xs ${STATUS_COLOR[rec.status] || "text-zinc-400"}`}>{slabel(rec.status)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <EntityLinkManager linkType="promise" recordId={id} note="Explicitly link canonical Entity Profiles (promisor, party, administration, ministry, agency, company, etc.). Linking never changes fulfilment status, progress, deadlines, costs or evidence." />
        </div>

        <div className="space-y-4">
          <Section title="Details">
            <dl className="text-sm space-y-2">
              {[["Institution", rec.institution], ["Date made", rec.date_made], ["Location", rec.location],
                ["Topic", rec.topic], ["Region", rec.region], ["Scope", rec.geo_scope], ["Type", rec.promise_type],
                ["Deadline", rec.deadline], ["Beneficiaries", rec.beneficiaries]].map(([k, v]) => (
                <div key={k}><dt className="label-caps text-zinc-500">{k}</dt><dd className="text-zinc-300">{v || "—"}</dd></div>
              ))}
              <div><dt className="label-caps text-zinc-500">Cost</dt><dd className="text-zinc-300" data-testid="pr-cost">{rec.original_cost != null ? `${rec.currency} ${rec.original_cost.toLocaleString()}` : "—"}{rec.current_cost != null ? ` → ${rec.currency} ${rec.current_cost.toLocaleString()}` : ""}</dd></div>
              <div><dt className="label-caps text-zinc-500">Group</dt>
                <dd><select data-testid="pr-group-detail" className={inputCls} value={rec.group_id || ""} onChange={(e) => saveField({ group_id: e.target.value }, "Group updated")}>
                  <option value="">None</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
                </select></dd>
              </div>
            </dl>
          </Section>

          <Section title="History (audit)">
            <ul className="space-y-2 text-xs" data-testid="pr-audit">
              {rec.audit_history.length === 0 && <li className="text-zinc-600">No activity.</li>}
              {rec.audit_history.map((a) => (
                <li key={a.id} className="text-zinc-400">
                  <span className="font-mono-data text-emerald-400">{a.action}</span>
                  {a.after?.status && <span> → {slabel(a.after.status)}</span>}
                  {a.after?.deadline && <span> → {a.after.deadline}</span>}
                  <span className="text-zinc-600"> · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>

      <Modal open={progressModal} onClose={() => setProgressModal(false)} title="Record Progress Assessment">
        <div className="space-y-4">
          <Field label="New / current status">
            <select data-testid="pr-progress-status" className={inputCls} value={progressForm.status} onChange={(e) => setProgressForm({ ...progressForm, status: e.target.value })}>
              {(meta?.statuses || []).map((s) => <option key={s} value={s}>{slabel(s)}</option>)}
            </select>
          </Field>
          <Field label="Progress summary"><textarea data-testid="pr-progress-summary" className={inputCls} rows={3} value={progressForm.progress_summary} onChange={(e) => setProgressForm({ ...progressForm, progress_summary: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Completion %"><input type="number" min={0} max={100} data-testid="pr-progress-pct" className={inputCls} value={progressForm.completion_pct} onChange={(e) => setProgressForm({ ...progressForm, completion_pct: e.target.value })} /></Field>
            <Field label="Analyst notes"><input data-testid="pr-progress-notes" className={inputCls} value={progressForm.notes} onChange={(e) => setProgressForm({ ...progressForm, notes: e.target.value })} /></Field>
          </div>
          {!rec.has_supporting_evidence && meta?.assessment_statuses?.includes(progressForm.status) && <p className="text-xs text-amber-400">A fulfilment assessment ({slabel(progressForm.status)}) requires at least one linked source or Claims & Evidence record.</p>}
          <Btn data-testid="pr-progress-save" onClick={submitProgress} className="w-full justify-center">Save Progress</Btn>
        </div>
      </Modal>

      <Modal open={reviseModal} onClose={() => setReviseModal(false)} title="Revise Deadline / Cost">
        <div className="space-y-4">
          <Field label="Deadline"><input type="date" data-testid="pr-revise-deadline" className={inputCls} value={reviseForm.deadline} onChange={(e) => setReviseForm({ ...reviseForm, deadline: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Original cost"><input type="number" data-testid="pr-revise-orig" className={inputCls} value={reviseForm.original_cost} onChange={(e) => setReviseForm({ ...reviseForm, original_cost: e.target.value })} /></Field>
            <Field label="Current cost"><input type="number" data-testid="pr-revise-curr" className={inputCls} value={reviseForm.current_cost} onChange={(e) => setReviseForm({ ...reviseForm, current_cost: e.target.value })} /></Field>
          </div>
          <Btn data-testid="pr-revise-save" onClick={submitRevise} className="w-full justify-center">Save Revision</Btn>
        </div>
      </Modal>

      <Modal open={!!linkModal} onClose={() => setLinkModal(null)} title={`Link ${linkModal?.type?.replace("_", " ")}`} wide>
        {linkModal && (
          <div className="space-y-3">
            <div className="max-h-80 overflow-y-auto space-y-1" data-testid="pr-link-options">
              {linkModal.options.length === 0 && <p className="text-xs text-zinc-600">Nothing available. Create one in its own workspace first.</p>}
              {linkModal.options.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm border border-zinc-800 px-3 py-2 rounded-sm cursor-pointer hover:border-zinc-600">
                  <input type="checkbox" checked={linkModal.selected.includes(o.id)} onChange={() => toggleSel(o.id)} />
                  <span className="truncate">{LINK_MAP[linkModal.type].label(o)}</span>
                </label>
              ))}
            </div>
            <Btn data-testid="pr-save-links" onClick={saveLinks} className="w-full justify-center">Save Links</Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}
